import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { AuthShell } from "@/pages/Login";
import { apiForgotPassword } from "@/lib/falApi";

function fmtErr(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const [devLink, setDevLink] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const res = await apiForgotPassword(email);
      setSent(true);
      if (res?.dev_reset_link) setDevLink(res.dev_reset_link);
    } catch (err) {
      toast.error(fmtErr(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (sent) {
    return (
      <AuthShell title="Check your email" subtitle="If an account exists, a reset link is on its way">
        <div className="rounded-lg border border-zinc-800 bg-[#121212] p-5 text-center" data-testid="forgot-sent">
          <MailCheck className="h-8 w-8 text-[#faff69] mx-auto" />
          <p className="text-sm text-zinc-300 mt-3">We've sent a password reset link to <span className="text-white font-medium">{email}</span>. The link expires in 1 hour.</p>
          {devLink && (
            <div className="mt-4 text-left">
              <p className="text-[11px] font-mono text-zinc-500 mb-1">Email isn't configured yet — use this link:</p>
              <a href={devLink} data-testid="dev-reset-link" className="text-[11px] font-mono text-[#faff69] break-all hover:underline">{devLink}</a>
            </div>
          )}
        </div>
        <Link to="/login" className="flex items-center justify-center gap-1.5 text-sm text-zinc-400 hover:text-[#faff69] mt-6" data-testid="back-to-login">
          <ArrowLeft className="h-4 w-4" /> Back to log in
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Reset password" subtitle="Enter your email and we'll send you a reset link">
      <form onSubmit={submit} className="space-y-3">
        <Input data-testid="forgot-email" type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)}
          className="bg-[#121212] border-zinc-800 focus-visible:ring-[#faff69]" />
        <Button type="submit" disabled={busy} data-testid="forgot-submit"
          className="w-full bg-[#faff69] text-black hover:bg-[#e6eb52] font-semibold">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send reset link"}
        </Button>
      </form>
      <p className="text-sm text-zinc-400 mt-5">Remembered it? <Link to="/login" className="text-[#faff69] hover:underline" data-testid="to-login">Log in</Link></p>
      <p className="text-[11px] text-zinc-600 mt-3">Signed up with Google? Just use "Continue with Google" — no password needed.</p>
    </AuthShell>
  );
}
